import { ElementRef, MutableRefObject } from 'react';
import styled from 'styled-components';

import { useGlobalStore } from '../../state/globalStore';
import { CustomFieldTypes, FORM_LAYOUT_OPTIONS } from '../../utils/constants';
import { getValidator } from '../../utils/helper';
import { Field, FieldOption } from '../../utils/types';
import { Checkbox, Dropdown, Input } from '../elements';
import { StylableCheckbox } from '../elements/StylableCheckbox';

export const CurrentFieldItem = ({
  field,
  value,
  onSaveValue,
  fieldsRef,
  multiStepItemWrapperRefs,
  index,
  onEnterKeyPressed,
  onClearPrefill,
  show = false,
  autoFocus = false,
  animationDirection = 'up',
  errorMessage,
  prefillField = false,
  describedBy,
}: {
  field: Field | undefined;
  value: boolean | string | FieldOption | undefined;
  onSaveValue: (value: boolean | string | FieldOption | undefined) => void;
  index: number;
  fieldsRef: (
    | ElementRef<typeof Input>
    | ElementRef<typeof Dropdown>
    | ElementRef<typeof Checkbox>
    | null
  )[];
  multiStepItemWrapperRefs: MutableRefObject<(HTMLDivElement | null)[] | null>;
  onEnterKeyPressed: (value: string | FieldOption | undefined) => void;
  show?: boolean;
  autoFocus: boolean;
  animationDirection?: 'up' | 'down';
  errorMessage?: string;
  prefillField?: boolean;
  onClearPrefill?: () => void;
  describedBy?: string;
}) => {
  const layout = useGlobalStore((state) => state.leadCapture.layout);

  if (!field) {
    return null;
  }

  const renderFieldComponent = () => {
    const { type, name, metadata, isRequired } = field;
    switch (type) {
      case CustomFieldTypes.Text:
        return (
          <Input
            onBlur={onSaveValue}
            onEnterKeyPressed={onEnterKeyPressed}
            autoFocus={autoFocus}
            ref={(el) => {
              fieldsRef[index] = el;
            }}
            value={typeof value === 'string' ? value : undefined}
            placeholder={name}
            required={isRequired}
            describedBy={describedBy}
            validator={prefillField ? () => undefined : getValidator(name, isRequired)}
            errorMessage={errorMessage}
            prefillValue={prefillField ? field.default : undefined}
            onClearPrefill={onClearPrefill}
          />
        );
      case CustomFieldTypes.Dropdown:
        return (
          <Dropdown
            onSelectedChanged={onSaveValue}
            autoFocus={autoFocus}
            ref={(el) => {
              fieldsRef[index] = el;
            }}
            selected={value ? (value as FieldOption) : undefined}
            placeholder={name}
            required={field.isRequired}
            options={metadata?.options || []}
            validator={prefillField ? () => undefined : getValidator(name, isRequired)}
            prefillValue={prefillField ? field.default : undefined}
            onClearPrefill={onClearPrefill}
          />
        );
      case CustomFieldTypes.Checkbox:
        return (
          <StylableCheckbox
            ref={(el) => {
              fieldsRef[index] = el;
            }}
            name={name}
            label={field.metadata?.description || ''}
            isRequired={field.isRequired}
            color={field.metadata?.color}
            onChange={(e) => onSaveValue(e.target.checked)}
          />
        );
      default:
        return null;
    }
  };

  if (layout === FORM_LAYOUT_OPTIONS.SINGLE_STEP_FORM) {
    return <FieldWrapperSS>{renderFieldComponent()}</FieldWrapperSS>;
  }

  return (
    <FieldWrapperMS
      ref={(el) => {
        if (multiStepItemWrapperRefs.current) multiStepItemWrapperRefs.current[index] = el;
      }}
      show={show}
      className={
        animationDirection === 'up'
          ? show
            ? 'animate-bottom-center'
            : 'animate-center-top'
          : show
            ? 'animate-top-center'
            : 'animate-center-bottom'
      }
    >
      {renderFieldComponent()}
    </FieldWrapperMS>
  );
};

const ANIMATION_DISTANCE = '30px';
const ANIMATION_DURATION = '.2s';

const FieldWrapperSS = styled.div`
  margin-top: 2px;
  margin-bottom: 16px;
  width: 100%;
`;

const FieldWrapperMS = styled.div<{ show: boolean }>`
  margin-top: 2px;
  margin-bottom: 16px;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  &.animate-center-top {
    animation-name: center-top;
    animation-duration: ${ANIMATION_DURATION};
    pointer-events: none;
  }
  &.animate-bottom-center {
    animation-name: bottom-center;
    animation-duration: ${ANIMATION_DURATION};
  }
  &.animate-top-center {
    animation-name: top-center;
    animation-duration: ${ANIMATION_DURATION};
  }
  &.animate-center-bottom {
    animation-name: center-bottom;
    animation-duration: ${ANIMATION_DURATION};
    pointer-events: none;
  }
  animation-fill-mode: forwards;
  @keyframes center-top {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-${ANIMATION_DISTANCE});
      opacity: 0;
      visibility: hidden;
    }
  }
  @keyframes bottom-center {
    from {
      transform: translateY(${ANIMATION_DISTANCE});
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes top-center {
    from {
      transform: translateY(-${ANIMATION_DISTANCE});
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes center-bottom {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(${ANIMATION_DISTANCE});
      opacity: 0;
      visibility: hidden;
    }
  }
`;

const StyledLabel = styled.div<{ color: string }>`
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
  color: ${({ color }) => color};
`;
